/**
 * The small set of primitives every screen is built from. Written once here so
 * a card, a stat tile or a status pill looks identical everywhere and a visual
 * change lands in one place.
 */
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Link } from 'react-router';
import { m, SPRING_SLIDE } from './motion.tsx';
import { createPortal } from 'react-dom';
import { popupHost, popupOrigin } from '../popup-host.ts';

export function Card({
  title,
  actions,
  children,
  caption,
  lead,
}: {
  /**
   * Content above the card's own heading, inside the same tile - the JMC
   * clinic page puts its 承認済み情報 tabs here, so the tabs and the panel they
   * switch read as one card instead of two stacked ones.
   */
  lead?: ReactNode;
  /**
   * Omitted by a card whose content carries its own heading - the profile
   * header is a name, a picture and a set of chips, and a card title above it
   * would say the person's name twice.
   */
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  caption?: string;
}) {
  return (
    <section className="card animate-fade-up">
      {lead}
      {(title || actions) && (
        <div className="card__head">
          {title && <h2 className="card__title">{title}</h2>}
          {actions && <div className="card__actions">{actions}</div>}
        </div>
      )}
      {caption && (
        <p className="muted small" style={{ marginTop: -6 }}>
          {caption}
        </p>
      )}
      {children}
    </section>
  );
}

/**
 * A figure lifted out of a card.
 *
 * There is no light variant and no `brand` flag any more. A tile is one thing
 * wherever it appears - the deep fill, white figures, the corner flower - and
 * a screen with two kinds of tile made the reader work out which kind they
 * were looking at before they could read the number.
 */
export function Stat({
  label,
  value,
  hint,
  small,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  small?: boolean;
}) {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className={`stat__value${small ? ' stat__value--sm' : ''}`}>{value}</div>
      {hint && <div style={{ marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

/**
 * A single quantity against its limit.
 *
 * The track is a lighter step of the *same* ramp as the fill, so the state reads
 * across the whole bar rather than only where the fill stops. Severity moves the
 * fill from brand to warning to danger - and never carries the meaning alone:
 * the figure beside it always says the same thing in words and numbers, which is
 * what a colour-blind reader, a grayscale print and a forced-colors mode all
 * fall back to.
 */
/**
 * A list of labelled facts about one thing.
 *
 * Profile screens were built out of `Stat` tiles, which is the wrong shape: a
 * tile is for a figure worth glancing at, and "email address" is not a figure.
 * This reads top to bottom like the record it is.
 */
export function Details({ children }: { children: ReactNode }) {
  return <dl className="details">{children}</dl>;
}

export function Detail({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <div className="details__row">
      <dt>{label}</dt>
      <dd>
        {children}
        {hint && <span className="field__hint">{hint}</span>}
      </dd>
    </div>
  );
}

export function Meter({
  used,
  limit,
  label,
}: {
  used: number;
  /** null means an unmetered allowance, which has no ratio to draw. */
  limit: number | null;
  label: string;
}) {
  if (limit === null || limit <= 0) return null;
  const ratio = Math.min(used / limit, 1);
  const state = ratio >= 1 ? 'danger' : ratio >= 0.8 ? 'warn' : 'ok';
  return (
    <div
      className={`meter meter--${state}`}
      role="meter"
      aria-valuenow={used}
      aria-valuemin={0}
      aria-valuemax={limit}
      aria-label={label}
    >
      <div className="meter__fill" style={{ inlineSize: `${(ratio * 100).toFixed(1)}%` }} />
    </div>
  );
}

export type Tone = 'default' | 'ok' | 'warn' | 'danger' | 'muted';

const TONE_CLASS: Record<Tone, string> = {
  default: '',
  ok: ' pill--ok',
  warn: ' pill--warn',
  danger: ' pill--danger',
  muted: ' pill--muted',
};

export function Pill({ tone = 'default', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`pill${TONE_CLASS[tone]}`}>{children}</span>;
}

/** Maps the domain's lifecycle states onto tones in exactly one place. */
/**
 * What each stored state is called on screen.
 *
 * The enum is the database's vocabulary. It was being printed straight into the
 * table - a clinic's 強み list read APPROVED / DRAFT / RETIRED down its 状態
 * column, in the middle of an otherwise Japanese page - and 「APPROVED」 is not a
 * word the person approving things uses. Unknown states still print as
 * themselves: a new one added server-side should look unfamiliar rather than
 * disappear.
 */
const STATUS_LABEL: Record<string, string> = {
  ACTIVE: '有効',
  APPROVED: '承認済み',
  DRAFT: '下書き',
  IN_REVIEW: '確認中',
  PAUSED: '停止中',
  RETIRED: '廃止済み',
  SUSPENDED: '停止',
  DISABLED: '無効',
  CANCELED: '解約',
  ARCHIVED: 'アーカイブ',
  PUBLISHED: '公開済み',
};

export function StatusPill({ status }: { status: string }) {
  const tone: Tone =
    status === 'ACTIVE' || status === 'APPROVED'
      ? 'ok'
      : status === 'DRAFT' || status === 'IN_REVIEW' || status === 'PAUSED'
        ? 'warn'
        : status === 'SUSPENDED' || status === 'DISABLED' || status === 'CANCELED'
          ? 'danger'
          : 'muted';
  return <Pill tone={tone}>{STATUS_LABEL[status] ?? status}</Pill>;
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ id: T; label: string }>;
  active: T;
  onChange: (id: T) => void;
}) {
  /*
   * Per instance, never a string literal. Two tab strips on one page sharing a
   * hard-coded layoutId share one pill, and it flies across the screen from one
   * strip to the other.
   */
  const pillId = useId();
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          className="tab"
          aria-selected={tab.id === active}
          onClick={() => onChange(tab.id)}
        >
          {tab.id === active && (
            // One element that moves between tabs, so the highlight travels
            // rather than blinking out and back in somewhere else.
            <m.span className="tab__pill" layoutId={pillId} transition={SPRING_SLIDE} />
          )}
          <span className="tab__label">{tab.label}</span>
        </button>
      ))}
    </div>
  );
}

/** An empty state names what will appear here, not merely that nothing has. */
export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

/**
 * Rows glide to their new place when the rows around them change.
 *
 * Every table built on `Table` used to jump: delete a row and everything under
 * it teleported up one row height on the next frame; add one and everything
 * below teleported down. New rows already fade in (listItemIn in styles.css);
 * this is the other half - the rows that MOVE.
 *
 * FLIP, in about twenty lines: remember each row's offsetTop after every
 * render, and when a row reappears somewhere else, start it at the old place
 * with a transform and let the Web Animations API take it to zero. offsetTop,
 * not getBoundingClientRect, so scrolling between renders is not mistaken for
 * a move. Rows framer-motion drives (`data-motion`) are left to it, and the
 * system's reduce-motion setting turns this off.
 *
 * React keeps a keyed row's DOM node across renders, which is what makes the
 * WeakMap lookup mean "the same row".
 */
function useRowGlide(body: { current: HTMLTableSectionElement | null }) {
  const seen = useRef(new WeakMap<HTMLElement, number>());
  useLayoutEffect(() => {
    const rows = body.current?.children;
    if (!rows) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    for (const row of Array.from(rows) as HTMLElement[]) {
      const top = row.offsetTop;
      const before = seen.current.get(row);
      seen.current.set(row, top);
      if (still || before === undefined || before === top || row.dataset.motion !== undefined) {
        continue;
      }
      row.animate(
        [{ transform: `translateY(${before - top}px)` }, { transform: 'translateY(0)' }],
        { duration: 240, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
      );
    }
  });
}

export function Table({
  head,
  children,
  caption,
}: {
  head: ReactNode;
  children: ReactNode;
  caption?: string;
}) {
  const body = useRef<HTMLTableSectionElement>(null);
  useRowGlide(body);
  return (
    <div className="table-wrap">
      <table>
        {caption && <caption>{caption}</caption>}
        <thead>
          <tr>{head}</tr>
        </thead>
        <tbody ref={body}>{children}</tbody>
      </table>
    </div>
  );
}

/**
 * The clickable target inside a `<tr className="row-link">`.
 *
 * Renders as plain text; its click area is stretched across the whole row by
 * CSS. One per row — a second stretched anchor would fight the first.
 */
export function RowLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link className="row-link__target" to={to}>
      {children}
    </Link>
  );
}

/**
 * Make a whole row open something that is not a link.
 *
 * `RowLink` covers the case where the row leads to a URL: a stretched anchor,
 * which keeps the middle-click, the copy-link and the status bar a link is
 * supposed to have. This is the other half - a row whose action is a dialog, an
 * expansion, an edit - where there is no href to stretch and the row itself has
 * to take the click.
 *
 * Two things it must not break, both of which the first attempt at this did:
 *
 *   the controls INSIDE the row. A press on 編集 or 削除 must run that button and
 *   nothing else, so anything interactive under the pointer wins. Selecting text
 *   in a cell is not a click either, hence the collapsed-selection check.
 *
 *   the keyboard. A `<tr>` is not focusable and announces nothing, so it gets a
 *   tabindex, a role and Enter/Space - and only when the event started on the
 *   row rather than bubbling up from a control inside it.
 */
export function rowOpen(onOpen: () => void): {
  className: string;
  tabIndex: number;
  role: string;
  onClick: (event: MouseEvent<HTMLTableRowElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => void;
} {
  return {
    className: 'row-open',
    tabIndex: 0,
    role: 'button',
    onClick: (event) => {
      const target = event.target as HTMLElement;
      if (target.closest('button, a, input, select, textarea, label, summary, details')) return;
      // A drag that selected text in a cell is a read, not a press.
      if (!(window.getSelection()?.isCollapsed ?? true)) return;
      onOpen();
    },
    onKeyDown: (event) => {
      if (event.target !== event.currentTarget) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      onOpen();
    },
  };
}

/**
 * The row's secondary actions, behind one glyph.
 *
 * A row has one thing it is for - opening - and usually one thing that must not
 * be pressed by accident. Putting both on the row as buttons gives them equal
 * weight and makes the destructive one the easiest target after the link; a
 * menu keeps it reachable and stops it competing.
 *
 * `<details>` rather than a hand-built popover: open and close, Escape, and
 * keyboard reach are the browser's, and the one thing it does not do - closing
 * when something else is clicked - is six lines here.
 *
 * The list is `position: fixed` and placed on open, because a table scrolls
 * horizontally and an absolutely positioned menu inside it would be clipped by
 * that scroll container.
 */
export function RowMenu({ label = '操作', children }: { label?: string; children: ReactNode }) {
  const ref = useRef<HTMLDetailsElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  /*
   * Mirrors the element's own `open`, because the list is portalled and React
   * has to know whether to render it at all. Without this every row on the
   * screen puts a hidden menu into `document.body` and leaves it there.
   */
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const details = ref.current;
    if (!details) return;
    const close = (event: Event) => {
      if (!details.open) return;
      if (!(event.target instanceof Node)) return;
      if (details.contains(event.target)) return;
      /*
       * The list is PORTALLED, so it is not inside `details` - and a pointerdown
       * on one of its own buttons was therefore treated as a click elsewhere.
       * The menu closed, React unmounted the button, and the `click` that would
       * have followed had nothing left to land on: every action in every row
       * menu did nothing at all under a real mouse (a synthetic `.click()` skips
       * pointerdown, which is why it looked fine when driven).
       */
      if (listRef.current?.contains(event.target)) return;
      details.open = false;
    };
    document.addEventListener('pointerdown', close);
    // A scroll moves the row out from under a fixed menu, so it closes instead.
    document.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('scroll', close, true);
    };
  }, []);

  /**
   * Where the list goes, measured from the BUTTON.
   *
   * It used to measure the `<details>` and drop the list straight below it with
   * no other thought, which went wrong in three ways at once: for one frame the
   * list rendered with no coordinates at all and painted at the top of the page
   * before jumping into place; near the foot of a long table it opened downward
   * off the bottom of the window; and a menu wider than the cell ran off the
   * left edge on a narrow screen.
   *
   * So: the summary is the anchor, the list is measured before it is shown, it
   * flips above the button when there is no room below, and it is clamped to the
   * viewport on both sides. Right-aligned to the button, because the button is
   * at the end of the row and a menu growing rightward would leave the screen.
   */
  const place = () => {
    const summary = ref.current?.querySelector('summary');
    if (!summary) return;
    const box = summary.getBoundingClientRect();
    const height = listRef.current?.offsetHeight ?? 0;
    const width = listRef.current?.offsetWidth ?? 160;
    const below = window.innerHeight - box.bottom;
    const flip = height > 0 && below < height + 12 && box.top > below;
    /*
     * Left, not right, and corrected for the host.
     *
     * The list is portalled to the popup host, which is `document.body` unless a
     * `<dialog>` is open - and a dialog is positioned, so a fixed child inside
     * it resolves against the dialog rather than the viewport. popupOrigin is
     * that shift, and it is zero everywhere else. Expressed as `left` because
     * that is the axis popupOrigin reports, and the right edge is just
     * `box.right - width`: the menu hangs from the button's right edge, since
     * the button is at the end of the row and a menu growing rightward would
     * leave the screen.
     */
    const origin = popupOrigin(popupHost(summary));
    setAt({
      top: (flip ? Math.max(8, box.top - height - 4) : box.bottom + 4) - origin.top,
      // Never off the left: on a narrow window the row's last cell can sit
      // closer to the edge than the menu is wide.
      left: Math.max(8, Math.min(box.right - width, window.innerWidth - width - 8)) - origin.left,
    });
  };

  return (
    <details
      className="rowmenu"
      ref={ref}
      onToggle={(event) => {
        setOpen(event.currentTarget.open);
        if (!event.currentTarget.open) return setAt(null);
        /*
         * Twice: once to place it from the anchor, and again once the browser
         * has laid the list out and its height is known - which is what decides
         * whether it fits below. The first pass is why it never paints at the
         * document's origin first.
         */
        place();
        requestAnimationFrame(place);
      }}
    >
      <summary aria-label={label} title={label}>
        ⋯
      </summary>
      {/*
        Portalled out of the row, like every other popup here.
        
        `position: fixed` is only fixed to the viewport when no ancestor
        establishes a containing block - and `.stagger > *` gives every card a
        `fadeInUp` whose transform persists under `animation-fill-mode: both`.
        Inside that, the list was laid out against the CARD: it landed tens of
        pixels below and left of the ⋯ that opened it, which is the offset in the
        screenshot. Moving it to the popup host takes it out from under any such
        ancestor; popupOrigin then handles the one host that legitimately shifts
        it, a `<dialog>`. See popup-host.ts.
      */}
      {open &&
        createPortal(
          <div
            ref={listRef}
            className="rowmenu__list"
            style={at ? { top: at.top, left: at.left } : { visibility: 'hidden' }}
            // A press inside the menu closes it. The portal moved these buttons
            // out of the `<details>`, so the element's own click-to-close no
            // longer covers them.
            onClick={() => {
              if (ref.current) ref.current.open = false;
            }}
          >
            {children}
          </div>,
          popupHost(ref.current),
        )}
    </details>
  );
}

/**
 * A button with press feedback. `whileTap` scales on pointer *and* keyboard
 * activation, and the spring settles rather than snapping back, which is what
 * separates a considered UI from one that merely moves.
 */
export function MotionButton({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  variant?: 'primary' | 'ghost';
  disabled?: boolean;
}) {
  return (
    // Press feedback is a CSS :active transform - see .btn. A component that only
    // scales in place must not pull framer-motion in with it.
    <button
      type={type}
      className={`btn${variant === 'ghost' ? ' btn--ghost' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

export function Checklist({ items }: { items: string[] }) {
  return (
    <ul className="checklist">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

/** Consistent date rendering; the API returns ISO strings throughout. */
export const jaDate = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleDateString('ja-JP') : '—';

/**
 * A plan limit as text. An absent key is unlimited, and says so.
 *
 * Shared by the plan editor and the clinic detail: a dash would read as "not
 * loaded" on one screen and "no ceiling" on the other, which are opposite
 * answers to the only question the number is asked.
 */
export const limitText = (value: number | undefined) =>
  value === undefined ? '無制限' : jaNumber(value);

export const jaNumber = (value: number | string | null | undefined) =>
  value === null || value === undefined ? '—' : Number(value).toLocaleString('ja-JP');
